import { api } from './client'
import type { ApiEnvelope, PaginatedResponse, Unit, UIConfigPayload } from '@/types/api'

export const LEVEL_STRING_TO_NUMBER: Record<string, number> = {
    "Cấp Tỉnh/Thành phố": 1,
    "Cấp Phường/Xã": 2,
}

export interface UnitQueryParams {
    keySearch?: string
    status?: number
    fromDate?: string
    toDate?: string
}

interface SpringPage<T> {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

function adaptPage<T>(
    raw: SpringPage<T> | PaginatedResponse<T>,
): PaginatedResponse<T> {
    if ('items' in raw) return raw
    return {
        items: raw.content,
        total: raw.totalElements,
        page: raw.number + 1,
        limit: raw.size,
        totalPages: raw.totalPages,
    }
}

/**
 * Fetch list of units with list table
 */
export async function fetchUnitsList(
    page: number = 0,
    pageSize: number = 10,
    params: UnitQueryParams = {},
    signal?: AbortSignal
): Promise<PaginatedResponse<Unit>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<Unit>>>('/v1/unit-management', {
            params: { page, size: pageSize, ...params },
            signal
        })
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách đơn vị.';
        throw new Error(message);
    }
}

/**
 * Fetch list of units with filters and select
 */
export async function fetchUnits(
    page: number = 0,
    pageSize: number = 10,
    params: UnitQueryParams = {},
    signal?: AbortSignal
): Promise<PaginatedResponse<Unit>> {
    try {
        const response = await api.get<ApiEnvelope<SpringPage<Unit>>>('/v1/unit-management/list-filter', {
            params: { page, size: pageSize, ...params },
            signal
        })
        return adaptPage(response.data.responseData)
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách đơn vị.';
        throw new Error(message);
    }
}

/**
 * Get unit by ID
 */
export async function getUnitById(id: string, signal?: AbortSignal): Promise<Unit> {
    try {
        const response = await api.get<ApiEnvelope<Unit>>(`/v1/unit-management/${id}`, { signal })
        return response.data.responseData
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy thông tin đơn vị.';
        throw new Error(message);
    }
}

/**
 * Create a new unit
 */
export async function createUnit(
    data: Omit<Unit, 'id' | 'createdAt' | 'updatedAt' | 'status'>
): Promise<ApiEnvelope<Unit>> {
    try {
        const response = await api.post<ApiEnvelope<Unit>>('/v1/unit-management', data)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi tạo đơn vị.';
        throw new Error(message);
    }
}

/**
 * Update unit details
 */
export async function updateUnit(
    id: string,
    data: Partial<Omit<Unit, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<ApiEnvelope<Unit>> {
    try {
        const response = await api.put<ApiEnvelope<Unit>>(`/v1/unit-management/${id}`, data)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi cập nhật đơn vị.';
        throw new Error(message);
    }
}

/**
 * Delete a unit
 */
export async function deleteUnit(id: string): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.delete<ApiEnvelope<any>>(`/v1/unit-management/${id}`)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi xóa đơn vị.';
        throw new Error(message);
    }
}

/**
 * Reset admin password for a unit
 */
export async function resetUnitPassword(id: string): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.post<ApiEnvelope<any>>(`/v1/unit-management/${id}/reset-password`)
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi khôi phục mật khẩu đơn vị.';
        throw new Error(message);
    }
}

/**
 * Resend the activation email for a unit.
 */
export async function sendUnitActivationMail(unitId: string): Promise<ApiEnvelope<any>> {
    try {
        const response = await api.post<ApiEnvelope<any>>(
            '/v1/unit-management/send-activate-mail',
            { unitId },
        )
        return response.data
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi gửi lại mail kích hoạt đơn vị.';
        throw new Error(message);
    }
}

/**
 * Lấy danh sách ảnh nền
 */
export async function getUnitBackgrounds(signal?: AbortSignal) {
    try {
        const response = await api.get<ApiEnvelope<any[]>>('/v1/unit-management/get-background', { signal });
        return response.data.responseData;
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy danh sách ảnh nền.';
        throw new Error(message);
    }
}

/**
 * Lấy cấu hình giao diện cho app
 */
export async function getUnitUIConfig(id: string, signal?: AbortSignal) {
    try {
        const response = await api.get<ApiEnvelope<any>>(`/v1/unit-management/${id}/ui-config/detail`, { signal });
        return response.data.responseData;
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lấy cấu hình giao diện.';
        throw new Error(message);
    }
}

/**
 * Cấu hình giao diện cho app
 */
export async function updateUnitUIConfig(id: string, data: UIConfigPayload) {
    try {
        const response = await api.post<ApiEnvelope<any>>(`/v1/unit-management/${id}/ui-config`, data);
        return response.data;
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi lưu cấu hình giao diện.';
        throw new Error(message);
    }
}

/**
 * Tải lên ảnh nền 
 */
export async function uploadUnitBackground(files: File[]) {
    try {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        const response = await api.post<ApiEnvelope<any[]>>('/v1/unit-management/upload-background', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi tải lên ảnh nền.';
        throw new Error(message);
    }
}

/**
 * Xóa ảnh nền khỏi danh sách
 */
export async function deleteUnitBackground(id: string) {
    try {
        const response = await api.delete<ApiEnvelope<any>>('/v1/unit-management/delete-background', {
            data: {
                fileIds: [id]
            }
        });
        return response.data;
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi xóa ảnh nền.';
        throw new Error(message);
    }
}